<template>
  <b-card class="mb-2">
    <b-card-header class="py-1 px-2">
      <strong>{{ slotname(filedef.name) }}</strong>
      <b-badge class="ml-2" :variant="filedef.required ? 'primary' : 'secondary'">
        {{ filedef.required ? t("flow_required") : t("flow_optional") }}
      </b-badge>
      <small class="text-muted ml-2">{{ filedef.table }}</small>
    </b-card-header>
    <b-card-body class="py-2 px-2">
      <b-form-radio-group v-model="mode" :options="modes" buttons button-variant="outline-secondary" size="sm"
        class="mb-2"></b-form-radio-group>

      <div v-if="mode === 'xlsx'">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group :label="t('slot_file_label')" label-size="sm">
              <b-form-file :ref="'xlsx_' + index" accept=".xlsx" no-drop :multiple="false" size="sm"
                @change="xlsx_selected"></b-form-file>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6" v-if="cachednames.length">
            <b-form-group :label="t('slot_reuse_label')" label-size="sm">
              <b-form-select v-model="reuse" :options="reuseoptions" size="sm" @change="reuse_selected"></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row v-if="filename && sheetnames.length">
          <b-col cols="12" md="6">
            <b-form-group :label="t('slot_sheet_label')" label-size="sm">
              <b-form-select v-model="sheetname" :options="sheetnames" size="sm" @change="convert"></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <div v-else>
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group :label="t('slot_csv_file_label')" label-size="sm">
              <b-form-file accept=".csv,.txt" no-drop :multiple="false" size="sm" @change="csv_selected"></b-form-file>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group :label="t('slot_csv_paste_label')" label-size="sm">
          <b-form-textarea v-model="pasted" rows="6" size="sm" :debounce="400" class="text-monospace"></b-form-textarea>
        </b-form-group>
      </div>

      <small v-if="slothint(filedef.name)" class="text-muted d-block mb-2">{{ slothint(filedef.name) }}</small>
      <small v-else-if="flowreservation" class="text-muted d-block mb-2">{{ t("slot_counter_hint") }}</small>

      <b-alert :show="!!localerror" variant="danger" class="py-1 px-2 mb-2">
        <i class="fa fa-exclamation-circle mr-1"></i>{{ localerror }}
      </b-alert>

      <div v-if="filedef.csv && !localerror">
        <div class="small mb-1">{{ summary }}</div>
        <div class="mb-2">
          <small class="text-muted mr-1">{{ t("slot_columns_label") }}:</small>
          <b-badge v-for="(h, i) in filedef.headers" :key="i" variant="light" class="mr-1 mb-1">{{ h }}</b-badge>
        </div>

        <b-card no-body class="mb-0">
          <b-card-header class="py-1 px-2">
            <small><strong>{{ t("controls_title") }}</strong> {{ t("controls_hint") }}</small>
          </b-card-header>
          <b-card-body class="py-2 px-2">
            <b-row>
              <b-col cols="12" md="4">
                <b-form-group :label="t('controls_rows_label')" label-size="sm">
                  <b-form-input v-model="expected_rows" type="number" min="0" size="sm"
                    :state="rowsstate"></b-form-input>
                  <b-form-invalid-feedback :state="rowsstate">{{ t("controls_rows_invalid") }}</b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
            <b-row v-for="(tot, i) in totals" :key="i">
              <b-col cols="12" md="5">
                <b-form-group :label="t('controls_total_label')" label-size="sm">
                  <b-form-select v-model="tot.column" :options="columnoptions" size="sm"></b-form-select>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="4">
                <b-form-group :label="t('controls_amount_label')" label-size="sm">
                  <b-form-input v-model="tot.expected" size="sm" :placeholder="t('controls_amount_hint')"
                    :state="totalstate(tot)"></b-form-input>
                  <b-form-invalid-feedback :state="totalstate(tot)">{{ t("controls_amount_invalid") }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import ExcelJS from "exceljs";
import { sheettocsv, csvinfo, defaultsheet, AMOUNT_RE, ROWS_RE } from "./ledgerloadercsv";
import { text, slotname, slothint } from "./ledgerloadercopy";

export default {
  name: "LedgerLoaderSlot",
  props: {
    // The page's slot record: { name, table, required, csv, headers, rows, source,
    // expected_rows, totals, error }. Named filedef because "slot" is reserved in Vue.
    filedef: { type: Object, required: true },
    index: { type: Number, required: true },
    dateformat: { type: String, default: null },
    // Shared workbook cache { [filename]: { workbook, sheetnames } } so one xlsx can
    // feed several slots with a different tab each.
    workbooks: { type: Object, default: () => ({}) },
    flowreservation: { type: Boolean, default: false },
  },
  data() {
    return {
      mode: "xlsx",
      filename: null,
      sheetname: null,
      reuse: null,
      pasted: "",
      skippaste: false, // set when a CSV file pick clears the textarea programmatically
      converted: null, // the workbook cache entry the last convert() used
      localerror: null,
      expected_rows: "",
      totals: [
        { column: null, expected: "" },
        { column: null, expected: "" },
      ],
      modes: [
        { value: "xlsx", text: text("slot_mode_xlsx") },
        { value: "csv", text: text("slot_mode_csv") },
      ],
    };
  },
  computed: {
    cachednames() {
      return Object.keys(this.workbooks || {});
    },
    reuseoptions() {
      return [{ value: null, text: "" }].concat(
        this.cachednames.map((n) => ({ value: n, text: n }))
      );
    },
    cachedentry() {
      return this.filename ? this.workbooks[this.filename] || null : null;
    },
    sheetnames() {
      return this.cachedentry ? this.cachedentry.sheetnames : [];
    },
    columnoptions() {
      return [{ value: null, text: "" }].concat(
        (this.filedef.headers || []).map((h) => ({ value: h, text: h }))
      );
    },
    rowsstate() {
      const v = String(this.expected_rows === null ? "" : this.expected_rows).trim();
      if (v === "") return null;
      return ROWS_RE.test(v);
    },
    summary() {
      const src = this.filedef.source || {};
      if (src.kind === "xlsx") {
        return text("slot_summary_xlsx", {
          rows: this.filedef.rows,
          cols: (this.filedef.headers || []).length,
          sheet: src.sheet,
          file: src.file,
        });
      }
      return text("slot_summary_csv", {
        rows: this.filedef.rows,
        cols: (this.filedef.headers || []).length,
        file: src.file || "pasted text",
      });
    },
  },
  watch: {
    mode() {
      // Switching intake mode drops what the other mode produced.
      this.localerror = null;
      this.filename = null;
      this.sheetname = null;
      this.reuse = null;
      this.converted = null;
      this.pasted = "";
      this.$emit("update", this.index, { csv: "", headers: [], rows: 0, error: null, source: null });
    },
    dateformat() {
      // Date cells in an xlsx must be re-rendered in the new layout. CSV is untouched.
      if (this.mode === "xlsx" && this.filename) this.convert();
    },
    // When the page replaces a cached workbook (the user re-chose a corrected file
    // in another slot), every slot showing a tab of that file converts again.
    cachedentry(entry) {
      if (this.mode !== "xlsx" || !entry || entry === this.converted) return;
      if (this.sheetname && !entry.sheetnames.includes(this.sheetname)) this.sheetname = defaultsheet(this.filedef.name, entry.sheetnames);
      this.convert();
    },
    pasted(v) {
      if (this.mode !== "csv") return;
      if (this.skippaste) {
        // csv_selected cleared the textarea after loading a file; the file's text
        // has already been emitted and must not be replaced with an empty slot.
        this.skippaste = false;
        return;
      }
      this.csv_text(v, { kind: "paste", file: "pasted text" });
    },
    expected_rows() {
      this.emitcontrols();
    },
    totals: {
      deep: true,
      handler() {
        this.emitcontrols();
      },
    },
  },
  methods: {
    t: text,
    slotname,
    slothint,
    totalstate(tot) {
      const col = tot.column || "";
      const amt = tot.expected === null || tot.expected === undefined ? "" : String(tot.expected).trim();
      if (!col && !amt) return null;
      if (!amt) return false;
      return AMOUNT_RE.test(amt);
    },
    emitcontrols() {
      const er = String(this.expected_rows === null ? "" : this.expected_rows).trim();
      this.$emit("update", this.index, {
        expected_rows: er === "" ? null : er,
        totals: this.totals.map((t) => ({ column: t.column, expected: t.expected })),
      });
    },
    async xlsx_selected(evt) {
      const file = evt && evt.target && evt.target.files ? evt.target.files[0] : null;
      if (!file) return;
      this.localerror = null;
      let buffer;
      try {
        buffer = await this.readbuffer(file);
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const sheetnames = workbook.worksheets
          .filter((w) => w.state !== "hidden" && w.state !== "veryHidden")
          .map((w) => w.name);
        this.$emit("workbook", file.name, { workbook, sheetnames });
        this.filename = file.name;
        this.reuse = null;
        this.sheetname = defaultsheet(this.filedef.name, sheetnames);
        // The cache entry lands on the parent on the next tick. The cachedentry
        // watcher normally converts first; this is the fallback if it has not.
        this.$nextTick(() => {
          if (this.converted !== this.cachedentry) this.convert();
        });
      } catch (e) {
        this.fail("READ_FAILED");
      }
    },
    readbuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file);
      });
    },
    reuse_selected(name) {
      if (!name) return;
      const entry = this.workbooks[name];
      if (!entry) return;
      this.filename = name;
      this.sheetname = defaultsheet(this.filedef.name, entry.sheetnames);
      this.convert();
    },
    convert() {
      const entry = this.cachedentry;
      if (!entry || !this.sheetname) return;
      this.converted = entry;
      const ws = entry.workbook.getWorksheet(this.sheetname);
      if (!ws) {
        this.fail("NO_HEADER");
        return;
      }
      const r = sheettocsv(ws, this.dateformat || "DD/MM/YYYY");
      if (r.error) {
        this.fail(r.error, r);
        return;
      }
      this.localerror = null;
      this.$emit("update", this.index, {
        csv: r.csv,
        headers: r.headers,
        rows: r.rows,
        error: null,
        source: { kind: "xlsx", file: this.filename, sheet: this.sheetname },
      });
    },
    fail(code, detail) {
      this.localerror = text("slot_" + code, detail || {});
      this.$emit("update", this.index, {
        csv: "",
        headers: [],
        rows: 0,
        error: code,
        source: null,
      });
    },
    csv_selected(evt) {
      const f = evt && evt.target && evt.target.files ? evt.target.files[0] : null;
      if (!f) return;
      const reader = new FileReader();
      reader.onload = () => {
        // Clearing the textarea fires the pasted watcher on the next tick; the flag
        // tells it to ignore that one change. Only needed when there was text to clear.
        if (this.pasted !== "") {
          this.skippaste = true;
          this.pasted = "";
        }
        this.csv_text(reader.result, { kind: "csv", file: f.name });
      };
      reader.onerror = () => this.fail("READ_FAILED");
      reader.readAsText(f, "UTF-8");
    },
    // The text is passed through unchanged; csvinfo only names the columns and
    // estimates the row count. The server is the parser of record.
    csv_text(textin, source) {
      const r = csvinfo(textin);
      if (r.error) {
        if (String(textin || "").length) this.fail(r.error);
        else {
          this.localerror = null;
          this.$emit("update", this.index, { csv: "", headers: [], rows: 0, error: null, source: null });
        }
        return;
      }
      this.localerror = null;
      this.$emit("update", this.index, {
        csv: textin,
        headers: r.headers,
        rows: r.rows,
        error: null,
        source,
      });
    },
  },
};
</script>

<style></style>
